import { useSkin } from './skin';
import { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import type { Locale } from '@blauturquesa/shared/locales';
import type { PublicPropertyDoc } from '@blauturquesa/shared/public-view';
import { Screen } from '../../shell/Screen';
import { Icon } from '../../components/Icon';
import { Loading } from '../../components/Loading';
import { EmptyState } from '../../components/EmptyState';
import { getPublicProperty } from './publicData';
import { rememberSource } from '../inquiries/source';
import { LazyMap } from '../map/LazyMap';
import { CaptionTag, captionText, formatSpec, Photo, pick, priceFromText } from './bits';

/** Fitxa pública (§10.2): hi arriben els QR. Una sola lectura (`publicProperties/{slug}`). */
export function PropertyPage() {
  const { slug = '' } = useParams();
  const { t, i18n } = useTranslation('public');
  const locale = i18n.language as Locale;
  const [view, setView] = useState<PublicPropertyDoc | null | undefined>(undefined);
  useSkin(view?.skin?.tokens);
  const [failed, setFailed] = useState(false);
  const [params] = useSearchParams();
  // D'on ve la visita (`?src=` del QR, §12): es desa a la consulta, si n'arriba a fer.
  useEffect(() => { rememberSource(params.get('src')); }, [params]);

  useEffect(() => {
    setView(undefined);
    setFailed(false);
    getPublicProperty(slug).then(setView, () => setFailed(true));
  }, [slug]);

  const actions = (
    <>
      <Link className="btn" to={`/p/${slug}/consulta`}><Icon name="message" size={20} /> {t('chat')}</Link>
      <Link className="btn-shiny grow" to={`/p/${slug}/consulta`}>{t('checkDates')}</Link>
    </>
  );

  if (failed) {
    return (
      <Screen title={t('title')} back="/explora">
        <EmptyState icon="globe" title={t('offline.title')}>{t('offline.body')}</EmptyState>
      </Screen>
    );
  }
  if (view === undefined) return <Screen title={t('title')} back="/explora"><Loading /></Screen>;
  if (view === null) {
    return (
      <Screen title={t('title')} back="/explora">
        <EmptyState icon="home" title={t('notFound.title')}>{t('notFound.body')}</EmptyState>
      </Screen>
    );
  }

  const src = view.sourceLocale;
  const title = pick(view.title, locale, src);
  const summary = pick(view.summary, locale, src);
  const description = pick(view.description, locale, src);
  const fallback = title.isFallback || summary.isFallback || description.isFallback;
  const place = [view.location.municipality, view.location.island].filter(Boolean).join(' · ');

  return (
    <Screen title={title.text} back="/explora" actions={actions}>
      {view.cover ? (
        <Link to={`/p/${view.slug}/fotos`} className="pp-hero" aria-label={t('openPhotos', { count: view.photos.length })}>
          <Photo photo={view.cover} sizes="(min-width: 1024px) 70vw, 100vw" locale={locale} source={src} eager />
          <CaptionTag text={captionText(view.cover.caption, { ...(view.priceFrom ? { price: view.priceFrom } : {}), modes: view.modes, locale, source: src, t })} className="pp-caption" />
          {view.photos.length > 0 && <span className="pp-count glass"><Icon name="image" size={16} /> {view.photos.length}</span>}
        </Link>
      ) : (
        <div className="photo-placeholder" />
      )}

      <header className="pp-head">
        <h1 lang={title.locale ?? undefined}>{title.text}</h1>
        {place && <p className="muted">{place}</p>}
        <div className="chips">{view.modes.map((m) => <span key={m} className="chip is-locked">{t(`modes.${m}`)}</span>)}</div>
        {priceFromText(view.priceFrom, view.modes, locale, t) && <p className="pp-price">{priceFromText(view.priceFrom, view.modes, locale, t)}</p>}
      </header>

      {fallback && <p className="muted small pp-fallback">{t('fallback')}</p>}
      {summary.text && <p className="lead" lang={summary.locale ?? undefined}>{summary.text}</p>}
      {description.text && <p className="pp-description" lang={description.locale ?? undefined}>{description.text}</p>}

      {view.location.point && (
        <section className="pp-map">
          <h2>{t('map.title')}</h2>
          <p className="muted small">{t(view.location.point.exact ? 'map.exact' : 'map.approx')}</p>
          <LazyMap
            label={t('map.label', { place })}
            center={view.location.point}
            zoom={view.location.point.exact ? 15 : 14}
            {...(view.location.point.exact ? { pins: [{ id: 'here', lat: view.location.point.lat, lng: view.location.point.lng }] } : { circle: view.location.point })}
          />
          {view.location.point.exact && (
            <p className="row">
              <a className="btn small" href={`https://www.google.com/maps/dir/?api=1&destination=${view.location.point.lat},${view.location.point.lng}`} target="_blank" rel="noopener noreferrer">
                <Icon name="map" size={16} /> {t('map.directions')}
              </a>
            </p>
          )}
        </section>
      )}

      {view.specs.length > 0 && (
        <section className="pp-specs">
          <h2>{t('specsTitle')}</h2>
          {view.specs.map((g) => (
            <div key={g.key} className="pp-spec-group">
              <h3>{pick(g.label, locale, src).text}</h3>
              <dl>
                {g.items.map((item) => {
                  const note = pick(item.note, locale, src).text;
                  return (
                    <div key={item.key} className="pp-spec">
                      <dt>{pick(item.label, locale, src).text}</dt>
                      <dd>{formatSpec(item, locale, src, t)}{note && <span className="muted small block">{note}</span>}</dd>
                    </div>
                  );
                })}
              </dl>
            </div>
          ))}
        </section>
      )}
    </Screen>
  );
}
